import { OMSUserSchema } from "@redotech/redo-fulfillment-group-common/oms-user";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum FulfillmentBatchStatus {
  PENDING_LABEL_PURCHASE = "pending",
  LABELS_CREATED = "labels_created",
  LABELS_PRINTED = "labels_printed",
  PENDING_MANUAL_FULFILLMENT = "pending_manual_fulfillment",
  MANUALLY_FULFILLED = "manually_fulfilled",
  FAILED = "failed",
  PICKED_UP = "picked_up",
  READY_FOR_PICKUP = "ready_for_pickup",
  PARTIALLY_PICKED_UP = "partially_picked_up",
}

export enum BatchHistoryTableColumnKeys {
  SELECTED = "selected",
  BATCH_ID = "batchId",
  BATCH_DATE = "batchDate",
  ORDERS = "orders",
  SHIPMENTS = "shipments",
  BATCH_STATUS = "batchStatus",
  ERRORS = "errors",
  CREATED_BY = "createdBy",
  NUMBER_OF_FULFILLMENTS = "numberOfFulfillments",
}

const FulfillmentBatchErrorSchema_ = z.object({
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
  message: z.string().meta({ privacy: privacy.free_text }),
});
export const FulfillmentBatchErrorSchema: z.ZodType<FulfillmentBatchError> =
  FulfillmentBatchErrorSchema_;

export interface FulfillmentBatchError extends z.infer<
  typeof FulfillmentBatchErrorSchema_
> {}

const FulfillmentBatchDocuments_ = z.object({
  shippingLabel: z.string().meta({ privacy: privacy.null }).nullish(),
  packingSlip: z.string().meta({ privacy: privacy.null }).nullish(),
  shippingLabel_packingSlip: z
    .string()
    .meta({ privacy: privacy.null })
    .nullish(),
  shippingLabel_returnLabel: z
    .string()
    .meta({ privacy: privacy.null })
    .nullish(),
  shippingLabel_returnLabel_packingSlip: z
    .string()
    .meta({ privacy: privacy.null })
    .nullish(),
  returnLabel: z.string().meta({ privacy: privacy.null }).nullish(),
  pickList: z.string().meta({ privacy: privacy.null }).nullish(),
  commercialInvoice: z.string().meta({ privacy: privacy.null }).nullish(),
  personalizedSlip: z.string().meta({ privacy: privacy.null }).nullish(),
});
export const FulfillmentBatchDocuments: z.ZodType<FulfillmentBatchDocuments> =
  FulfillmentBatchDocuments_;
export interface FulfillmentBatchDocuments extends z.infer<
  typeof FulfillmentBatchDocuments_
> {}

const FulfillmentBatchSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
    id: z.string().meta({ privacy: privacy.nonPii }),
    team: zExt.objectId().meta({ privacy: privacy.nonPii }),
    batchDate: z.date().meta({ privacy: privacy.nonPii }),
    orderNumbers: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.nonPii }),
    fulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.nonPii }),
    failedFulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    syncedOrderIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    errors: z
      .array(FulfillmentBatchErrorSchema_)
      .nullish()
      .meta({ privacy: privacy.structured }),
    boughtRates: z
      .record(
        z.string().meta({ privacy: privacy.null }),
        zExt.objectId().meta({ privacy: privacy.nonPii }),
      )
      .nullish()
      .meta({ privacy: privacy.null }),
    boughtReturnRates: z
      .record(
        z.string().meta({ privacy: privacy.null }),
        zExt.objectId().meta({ privacy: privacy.nonPii }),
      )
      .nullish()
      .meta({ privacy: privacy.null }),
    shipmentIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.nonPii }),
    batchStatus: createEnum(FulfillmentBatchStatus, {
      enumName: "FulfillmentBatchStatus",
    }).meta({ privacy: privacy.nonPii }),
    documents: FulfillmentBatchDocuments_.nullish().meta({
      privacy: privacy.structured,
    }),
    createdBy: OMSUserSchema.nullish().meta({ privacy: privacy.structured }),
    containsCommercialInvoice: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /** @deprecated Use each fulfillment group's settings. */
    notifyMarketplace: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    /** @deprecated Use each fulfillment group's settings. */
    fulfillmentDate: z.date().meta({ privacy: privacy.nonPii }).nullish(),
    insured: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    updatedAt: z.date().meta({ privacy: privacy.nonPii }).nullish(),
    local: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    packingSlipIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    packingSlipPrintedAt: z.date().meta({ privacy: privacy.nonPii }).nullish(),
    orderSummaryPrintedAt: z.date().meta({ privacy: privacy.nonPii }).nullish(),
    voidableFulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const FulfillmentBatchSchema: z.ZodType<FulfillmentBatch> =
  FulfillmentBatchSchema_;
export interface FulfillmentBatch extends z.infer<
  typeof FulfillmentBatchSchema_
> {}
export type FulfillmentBatchDocumentKind = keyof z.infer<
  typeof FulfillmentBatchDocuments_
>;
